import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import React, { useEffect } from 'react';
import { CloseIcon } from '../../assets/Icons';
import { isWebDevice } from '../../helpers/utils';

interface BottomModalProps {
  isOpen: boolean;
  height?: string;
  hideClose?: boolean;
  children: ReactNode;
  onClose: () => void;
}

const BottomModal: React.FC<BottomModalProps> = ({
  isOpen,
  onClose,
  children,
  hideClose = false,
  height = isWebDevice() ? '60%' : '92%',
}) => {
  // 'auto' lets the sheet size to its content (capped), instead of a fixed height.
  const isAutoHeight = height === 'auto';
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    window.history.pushState({ modalOpen: true }, '');
    const handlePopState = () => {
      onClose();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      const state = window.history.state as { modalOpen?: boolean } | null;
      if (state?.modalOpen) {
        window.history.back();
      }
    };
  }, [isOpen, onClose]);

  return (
    <>
      <div
        onClick={onClose}
        className={twMerge(
          'fixed inset-0 bg-black/50 z-97 transition-opacity duration-300',
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible',
        )}
      />
      <div
        style={isAutoHeight ? undefined : { height: `calc(${height})` }}
        className={twMerge(
          'fixed bottom-0 left-0 right-0 mx-auto max-w-[752px] z-98 rounded-t-2xl bg-up-black-5 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] transform transition-transform duration-300 ease-out',
          isAutoHeight ? 'max-h-[92%]' : '',
          isOpen ? 'translate-y-0' : 'translate-y-full',
        )}>
        {!hideClose && (
          <CloseIcon
            onClick={onClose}
            className="absolute right-2 top-5 bg-up-black-5 p-0.5 rounded-sm cursor-pointer w-7 h-7"
          />
        )}
        <div
          className={twMerge(
            'overflow-y-auto px-2 py-5 rounded-t-2xl',
            isAutoHeight ? 'max-h-[92vh]' : 'h-full',
          )}
          style={{
            overscrollBehavior: 'contain',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}>
          {isOpen && children}
        </div>
      </div>
    </>
  );
};

export default BottomModal;
